import { ExternalLink, Loader2, Send } from "lucide-react";
import { useState } from "react";

import {
  CardActionButton,
  DisconnectFooter,
  IntegrationCardShell,
  StatusPill,
} from "@/components/dashboard/IntegrationCardShell";

interface TelegramIntegrationPanelProps {
  telegramIntegration:
    | {
        connectedAt?: number;
        status: string;
        lastError?: string;
        config?: unknown;
      }
    | null
    | undefined;
  telegramUsers:
    | Array<{
        telegramFirstName?: string;
        telegramUsername?: string;
        telegramChatId: string;
        role: string;
      }>
    | undefined;
  onConnect: (args: { botToken: string }) => Promise<unknown>;
  onDisconnect: () => Promise<unknown>;
}

function BotTokenInput({
  value,
  onChange,
  onSubmit,
}: {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Paste your bot token"
      className="w-full rounded-full bg-home-ink/[0.05] px-4 py-2.5 font-body text-sm text-home-ink placeholder-home-ink/35 outline-none focus:ring-2 focus:ring-home-yellow dark:bg-white/10 dark:text-white dark:placeholder-white/35"
      onKeyDown={(e) => e.key === "Enter" && onSubmit()}
    />
  );
}

export function TelegramIntegrationPanel({
  telegramIntegration,
  telegramUsers,
  onConnect,
  onDisconnect,
}: TelegramIntegrationPanelProps) {
  const [botToken, setBotToken] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  const status = telegramIntegration?.status;
  const config = telegramIntegration?.config as {
    botUsername?: string;
    pairingDeepLink?: string;
    lastSyncError?: string | null;
  } | null;

  const owner = telegramUsers?.find((u) => u.role === "owner");

  async function handleConnect() {
    if (!botToken.trim()) return;
    setIsSubmitting(true);
    try {
      await onConnect({ botToken: botToken.trim() });
      setBotToken("");
    } catch {
      // Error reflected in integration status
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDisconnect() {
    await onDisconnect();
    setShowDisconnectConfirm(false);
  }

  // Disconnected or no integration
  if (!telegramIntegration || status === "disconnected") {
    return (
      <IntegrationCardShell
        icon={Send}
        title="Telegram"
        description="Chat with your agent from Telegram"
        footer={
          <div className="flex flex-col gap-2.5">
            <BotTokenInput value={botToken} onChange={setBotToken} onSubmit={handleConnect} />
            <CardActionButton
              tone="tonal"
              disabled={!botToken.trim() || isSubmitting}
              onClick={handleConnect}
            >
              {isSubmitting ? "Connecting…" : "Connect"}
            </CardActionButton>
          </div>
        }
      >
        <details className="mt-4 group">
          <summary className="cursor-pointer list-none font-body text-[13px] font-semibold text-home-ink/55 transition-colors hover:text-home-ink dark:text-white/55 dark:hover:text-white">
            <span className="mr-1 inline-block transition-transform group-open:rotate-90">
              ›
            </span>
            How to set up a bot
          </summary>
          <ol className="mt-2 space-y-1.5 pl-4 font-body text-[13px] leading-relaxed text-home-ink/55 dark:text-white/55">
            <li>1. In Telegram, message @BotFather</li>
            <li>
              2. Send{" "}
              <code className="rounded bg-home-ink/[0.06] px-1 font-mono text-[12px] dark:bg-white/10">
                /newbot
              </code>{" "}
              and follow the prompts
            </li>
            <li>3. Copy the bot token and paste it below</li>
          </ol>
        </details>
      </IntegrationCardShell>
    );
  }

  // Connecting
  if (status === "connecting") {
    return (
      <IntegrationCardShell
        icon={Send}
        title="Telegram"
        pill={<StatusPill variant="pending">Setting up…</StatusPill>}
        description="Chat with your agent from Telegram"
        footer={
          <div className="flex items-center justify-center gap-2 py-2.5 font-body text-sm text-home-ink/55 dark:text-white/55">
            <Loader2 className="h-4 w-4 animate-spin" />
            Configuring your bot — about 30 seconds
          </div>
        }
      />
    );
  }

  // Error — human copy only; the raw error stays server-side
  if (status === "error") {
    return (
      <IntegrationCardShell
        icon={Send}
        title="Telegram"
        pill={<StatusPill variant="attention">Action needed</StatusPill>}
        description="That token didn't work. Double-check it in @BotFather and try again."
        footer={
          <div className="flex flex-col gap-2.5">
            <BotTokenInput value={botToken} onChange={setBotToken} onSubmit={handleConnect} />
            <CardActionButton
              tone="primary"
              disabled={!botToken.trim() || isSubmitting}
              onClick={handleConnect}
            >
              {isSubmitting ? "Connecting…" : "Retry"}
            </CardActionButton>
          </div>
        }
      />
    );
  }

  // Connected
  return (
    <IntegrationCardShell
      icon={Send}
      title="Telegram"
      pill={<StatusPill variant="active">Connected</StatusPill>}
      description={
        owner && config?.botUsername
          ? `Paired with @${config.botUsername}`
          : "Chat with your agent from Telegram"
      }
      footer={
        <DisconnectFooter
          confirming={showDisconnectConfirm}
          disconnecting={false}
          onConfirmChange={setShowDisconnectConfirm}
          onDisconnect={handleDisconnect}
        />
      }
    >
      {owner && config?.botUsername && (
        <a
          href={`https://t.me/${config.botUsername}`}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-flex items-center gap-1.5 font-body text-[13px] font-semibold text-home-ink/70 underline-offset-2 hover:underline dark:text-white/70"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Open chat in Telegram
        </a>
      )}

      {/* Pairing step — only when the bot exists but no owner is paired yet */}
      {!owner && config?.pairingDeepLink && (
        <div className="mt-4 rounded-2xl bg-home-butter/50 p-4">
          <p className="font-body text-[13px] font-semibold text-home-ink">
            One step left — pair your account
          </p>
          <p className="mt-1 font-body text-[12px] leading-relaxed text-home-ink/60">
            Open Telegram to pair your account as the bot owner.
          </p>
          <a
            href={config.pairingDeepLink}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-home-ink px-4 py-2 font-body text-[13px] font-semibold text-home-cream transition-all hover:-translate-y-px"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Open in Telegram
          </a>
        </div>
      )}

      {/* Sync degradation — calm nudge, no raw error string */}
      {config?.lastSyncError && (
        <p className="mt-3 font-body text-[12px] leading-relaxed text-home-ink/55 dark:text-white/55">
          Having trouble syncing — if messages stop arriving, disconnect and
          reconnect the bot.
        </p>
      )}
    </IntegrationCardShell>
  );
}
